import React, { useEffect, useRef } from 'react';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import QRCodeStyling from 'qr-code-styling';
import { observer } from 'mobx-react';

const EmptyPlaceholder = () => {
  const border = useColorModeValue('gray.300', 'gray.600');
  const bg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.400', 'gray.500');
  const iconColor = useColorModeValue('#CBD5E0', '#4A5568');

  return (
    <Box
      w="256px"
      h="256px"
      borderRadius="lg"
      borderWidth="2px"
      borderStyle="dashed"
      borderColor={border}
      bg={bg}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={3}
    >
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
        <rect x="4"  y="4"  width="24" height="24" rx="3" fill={iconColor} />
        <rect x="9"  y="9"  width="14" height="14" rx="1" fill={bg === 'white' ? 'white' : '#1A202C'} />
        <rect x="12" y="12" width="8"  height="8"  rx="1" fill={iconColor} />
        <rect x="52" y="4"  width="24" height="24" rx="3" fill={iconColor} />
        <rect x="57" y="9"  width="14" height="14" rx="1" fill={bg === 'white' ? 'white' : '#1A202C'} />
        <rect x="60" y="12" width="8"  height="8"  rx="1" fill={iconColor} />
        <rect x="4"  y="52" width="24" height="24" rx="3" fill={iconColor} />
        <rect x="9"  y="57" width="14" height="14" rx="1" fill={bg === 'white' ? 'white' : '#1A202C'} />
        <rect x="12" y="60" width="8"  height="8"  rx="1" fill={iconColor} />
        <rect x="34" y="4"  width="6" height="6" rx="1" fill={iconColor} />
        <rect x="42" y="4"  width="6" height="6" rx="1" fill={iconColor} />
        <rect x="34" y="12" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="34" y="34" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="42" y="34" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="50" y="34" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="42" y="42" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="50" y="50" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="58" y="42" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="66" y="34" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="4"  y="34" width="6" height="6" rx="1" fill={iconColor} />
        <rect x="12" y="42" width="6" height="6" rx="1" fill={iconColor} />
      </svg>
      <Text fontSize="xs" color={textColor} textAlign="center" px={6} lineHeight="1.5">
        Enter content and click<br /><strong>Generate QR Code</strong>
      </Text>
    </Box>
  );
};

const QrPreview = observer(({ store }) => {
  const containerRef = useRef(null);
  const qrRef = useRef(null);
  const attachedToRef = useRef(null);

  const generatedPayload = store.generatedPayload;
  // Read styling options in the render function so MobX tracks design
  // observables and re-renders (and re-runs the effect) on any design change.
  const opts = generatedPayload
    ? { ...store.getQrStylingOptions(), data: generatedPayload }
    : null;

  useEffect(() => {
    if (!opts || !containerRef.current) return;

    const container = containerRef.current;

    if (!qrRef.current || attachedToRef.current !== container) {
      container.innerHTML = '';
      qrRef.current = new QRCodeStyling(opts);
      qrRef.current.append(container);
      attachedToRef.current = container;
    } else {
      qrRef.current.update(opts);
    }
  });

  if (!generatedPayload) {
    return <EmptyPlaceholder />;
  }

  return (
    <Box
      ref={containerRef}
      display="flex"
      alignItems="center"
      justifyContent="center"
      transition="transform 0.2s"
      _hover={{ transform: 'scale(1.02)' }}
    />
  );
});

export default QrPreview;
